import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import "../styles/padel-partner.css";

const DURATION = 19; // seconds
const RESTART_DELAY = 3000; // ms after end before loop restarts

// Mid-loop bid events (ms since start, new bid + leader)
const BID_EVENTS = [
  { at: 6000,  amt: "£20m", leader: "daz6 still leading", color: "#7a8fa0" },
  { at: 12000, amt: "£25m", leader: "You leading",       color: "#1ec8e0" },
  { at: 18000, amt: "£30m", leader: "daz6 leading",       color: "#7a8fa0" },
];

export default function PadelPartner() {
  const [timer, setTimer] = useState("00:19");
  const [bid, setBid] = useState("£15m");
  const [leader, setLeader] = useState("daz6 leading");
  const [leaderColor, setLeaderColor] = useState("#7a8fa0");
  const [barPct, setBarPct] = useState(100);
  const [toastShow, setToastShow] = useState(false);

  const startRef = useRef(null);
  const firedRef = useRef(new Set());
  const rafRef = useRef(null);
  const restartTimeoutRef = useRef(null);

  useEffect(() => {
    document.title = "Sport X — Padel Partnership";
    return () => { document.title = "Sport X"; };
  }, []);

  useEffect(() => {
    function reset() {
      startRef.current = null;
      firedRef.current = new Set();
      setTimer("00:19");
      setBid("£15m");
      setLeader("daz6 leading");
      setLeaderColor("#7a8fa0");
      setBarPct(100);
    }

    function loop(ts) {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const remaining = Math.max(0, DURATION - Math.floor(elapsed / 1000));
      const pct = Math.max(0, 100 - (elapsed / (DURATION * 1000)) * 100);

      setTimer("00:" + String(remaining).padStart(2, "0"));
      setBarPct(pct);

      for (const ev of BID_EVENTS) {
        if (!firedRef.current.has(ev.at) && elapsed >= ev.at) {
          firedRef.current.add(ev.at);
          setBid(ev.amt);
          setLeader(ev.leader);
          setLeaderColor(ev.color);
        }
      }

      if (elapsed < DURATION * 1000) {
        rafRef.current = requestAnimationFrame(loop);
      } else {
        setToastShow(true);
        setTimeout(() => setToastShow(false), 2200);
        restartTimeoutRef.current = setTimeout(() => {
          reset();
          rafRef.current = requestAnimationFrame(loop);
        }, RESTART_DELAY);
      }
    }

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (restartTimeoutRef.current) clearTimeout(restartTimeoutRef.current);
    };
  }, []);

  return (
    <div className="padel-partner-page" data-testid="padel-partner-page">

      {/* HEADER */}
      <header className="pp-header">
        <div className="pp-logo">SPORT <span>X</span></div>
        <div className="pp-header-label">Partner overview · Padel</div>
        <Link to="/" className="pp-header-cta" data-testid="pp-header-cta">Try the app →</Link>
      </header>

      {/* HERO */}
      <section className="pp-hero">
        <div className="pp-hero-eyebrow">Premier Padel · 2026</div>
        <h1>A sports auction game<br />for <em>padel fans.</em></h1>
        <p className="pp-hero-sub">
          Friends bid on real Premier Padel players. Compete as the matches play out. Every competition raises money for a good cause. No gambling. No advertising. No data exploitation.
        </p>
        <div className="pp-hero-values">
          <div className="pp-val-pill no">🚫 No gambling</div>
          <div className="pp-val-pill no">🚫 No advertising</div>
          <div className="pp-val-pill no">🚫 No data exploitation</div>
          <div className="pp-val-pill">✓ Free to play</div>
          <div className="pp-val-pill">✓ Cause-led</div>
          <div className="pp-val-pill">✓ Friends only</div>
        </div>
      </section>

      <hr className="pp-divider" />

      {/* HOW IT WORKS */}
      <section className="pp-section">
        <div className="pp-section-label">How it works</div>
        <div className="pp-section-title">The auction is the moment.<br />The sport does the rest.</div>

        {/* Live auction widget */}
        <div className="pp-widget-wrap">
          <div className="pp-widget-header">
            <div className="pp-widget-timer">{timer}</div>
            <div className="pp-live-badge"><div className="pp-live-dot"></div> LIVE</div>
          </div>
          <div className="pp-widget-player">
            <div className="pp-widget-flag">🇦🇷</div>
            <div className="pp-widget-name">Agustín Tapia</div>
            <div className="pp-widget-meta">RANKED #1 · PREMIER PADEL</div>
          </div>
          <div className="pp-widget-bid-label">Current highest bid</div>
          <div className="pp-widget-bid-amt">{bid}</div>
          <div className="pp-widget-leader" style={{ color: leaderColor }}>{leader}</div>
          <div className="pp-widget-bar">
            <div className="pp-widget-bar-fill" style={{ width: `${barPct}%` }}></div>
          </div>
          <div className="pp-widget-btns">
            <div className="pp-widget-btn">+£1m</div>
            <div className="pp-widget-btn">+£5m</div>
            <div className="pp-widget-btn">+£10m</div>
            <div className="pp-widget-btn">+£20m</div>
            <div className="pp-widget-btn">+£50m</div>
            <div className="pp-widget-btn pass">Pass</div>
          </div>
          <div className="pp-widget-avatars">
            <div className="pp-widget-avs">
              <div className="pp-widget-av" style={{ background: "#1ec8e0" }}>DA</div>
              <div className="pp-widget-av" style={{ background: "#4ade80" }}>GO</div>
              <div className="pp-widget-av" style={{ background: "#c084fc" }}>SK</div>
              <div className="pp-widget-av" style={{ background: "#f5c842" }}>MA</div>
            </div>
            <span className="pp-widget-av-label">4 of 6 friends · 3 online</span>
          </div>
          <div className="pp-widget-caption">Live auction — 30 seconds per player</div>
          <div className={`pp-toast ${toastShow ? "show" : ""}`}>🏆 Won by daz6</div>
        </div>

        <div className="pp-steps">
          <div className="pp-step">
            <div className="pp-step-num">01</div>
            <div className="pp-step-title">Create</div>
            <div className="pp-step-body">Pick a Premier Padel tournament. Name your competition. Invite your group with one link.</div>
          </div>
          <div className="pp-step">
            <div className="pp-step-num">02</div>
            <div className="pp-step-title">Auction</div>
            <div className="pp-step-body">Everyone gets a virtual budget. Bid against each other for real players. 30 seconds per player.</div>
          </div>
          <div className="pp-step">
            <div className="pp-step-num">03</div>
            <div className="pp-step-title">Follow</div>
            <div className="pp-step-body">Scores update automatically from real match results. No tinkering. No weekly management.</div>
          </div>
          <div className="pp-step">
            <div className="pp-step-num">04</div>
            <div className="pp-step-title">Win</div>
            <div className="pp-step-body">Best squad wins. A portion of every entry goes to a cause the winner chooses.</div>
          </div>
        </div>

        {/* Tour strip */}
        <div className="pp-tour-strip">
          <div className="pp-tour-pill active">🎾 Italy Major · June</div>
          <div className="pp-tour-pill">🎾 Valencia P1 · June</div>
          <div className="pp-tour-pill">🎾 Málaga P1 · July</div>
          <div className="pp-tour-pill">🎾 London P1 · Aug</div>
          <div className="pp-tour-pill">🎾 Madrid P1 · Sep</div>
          <div className="pp-tour-pill">🎾 Paris Major · Sep</div>
          <div className="pp-tour-pill">🎾 Barcelona Finals · Dec</div>
        </div>
      </section>

      <hr className="pp-divider" />

      {/* THE OPPORTUNITY */}
      <section className="pp-section">
        <div className="pp-section-label">The opportunity</div>
        <div className="pp-section-title">A market no one<br />has served yet.</div>

        <div className="pp-opportunity-grid">
          <div className="pp-opp-cell">
            <div className="pp-opp-num">30M</div>
            <div className="pp-opp-label"><strong>Padel players globally</strong>Growing 20–30% annually. Spain and Argentina the dominant markets. 26 Premier Padel events in 2026 across 18 countries.</div>
          </div>
          <div className="pp-opp-cell">
            <div className="pp-opp-num">0</div>
            <div className="pp-opp-label"><strong>Equivalent products</strong>No social auction competition game exists for padel fans anywhere. No gambling product dominates the space. Sport X is first.</div>
          </div>
          <div className="pp-opp-cell">
            <div className="pp-opp-num">26</div>
            <div className="pp-opp-label"><strong>Premier Padel events in 2026</strong>Each event is a natural Sport X competition anchor. Italy Major in three weeks. Valencia P1 the week after.</div>
          </div>
          <div className="pp-opp-cell">
            <div className="pp-opp-num">2M+</div>
            <div className="pp-opp-label"><strong>Premier Padel social following</strong>Passive audience following the tour. Sport X converts passive fans into active participants — competing together, raising money for good causes.</div>
          </div>
          <div className="pp-opp-cell full">
            <div className="pp-opp-statement">&ldquo;Sport X is the fan participation product the Premier Padel tour doesn&apos;t yet have — friends competing together around every event, all season, with a cause at the heart of it.&rdquo;</div>
          </div>
        </div>
      </section>

      <hr className="pp-divider" />

      {/* COMMERCIAL MODEL */}
      <section className="pp-section">
        <div className="pp-section-label">Commercial model</div>
        <div className="pp-section-title">Two models.<br />One shared conviction.</div>

        <div className="pp-model-grid">
          <div className="pp-model-card highlight">
            <div className="pp-model-tag">Model A — Charity partner</div>
            <div className="pp-model-title">Your cause. Your community. All season.</div>
            <div className="pp-model-desc">Sport X competitions run anchored to Premier Padel events all season. Your community competes together and raises money for your nominated cause every time they play — not just at your annual event.</div>
            <div className="pp-model-split">
              <div className="pp-split-item"><span className="pp-split-pct">90%</span><span className="pp-split-who">Your cause</span></div>
              <div className="pp-split-item"><span className="pp-split-pct">10%</span><span className="pp-split-who">Sport X</span></div>
            </div>
          </div>
          <div className="pp-model-card">
            <div className="pp-model-tag">Model B — Commercial partner</div>
            <div className="pp-model-title">Your audience. Your brand. New revenue.</div>
            <div className="pp-model-desc">Your audience plays Sport X competitions around Premier Padel events, branded under your name. You earn 25% of every entry fee they generate — no additional content required.</div>
            <div className="pp-model-split">
              <div className="pp-split-item"><span className="pp-split-pct">50%</span><span className="pp-split-who">Cause basket</span></div>
              <div className="pp-split-item"><span className="pp-split-pct">25%</span><span className="pp-split-who">Sport X</span></div>
              <div className="pp-split-item"><span className="pp-split-pct">25%</span><span className="pp-split-who">You</span></div>
            </div>
          </div>
        </div>

        {/* What's included */}
        <div className="pp-offer-grid">
          <div className="pp-offer-item">
            <span className="pp-offer-icon">🏷️</span>
            <div className="pp-offer-title">Branded competitions</div>
            <div className="pp-offer-body">Every competition carries your name — a [Partner] Sport X Challenge. Your identity, your community.</div>
          </div>
          <div className="pp-offer-item">
            <span className="pp-offer-icon">📊</span>
            <div className="pp-offer-title">Audience data</div>
            <div className="pp-offer-body">Participation rates, engagement by event, geographic spread — data that helps you understand your community.</div>
          </div>
          <div className="pp-offer-item">
            <span className="pp-offer-icon">💚</span>
            <div className="pp-offer-title">Cause alignment</div>
            <div className="pp-offer-body">Every Sport X competition raises money for a good cause. No gambling, no advertising, no exploitation.</div>
          </div>
          <div className="pp-offer-item">
            <span className="pp-offer-icon">⚡</span>
            <div className="pp-offer-title">Zero management</div>
            <div className="pp-offer-body">Sport X runs the platform. No event logistics, no tech integration, no staff resource required.</div>
          </div>
        </div>
      </section>

      <hr className="pp-divider" />

      {/* CALENDAR */}
      <section className="pp-section">
        <div className="pp-section-label">Immediate activation</div>
        <div className="pp-section-title">The calendar is already<br />running.</div>

        <div className="pp-calendar-list">
          <div className="pp-cal-row header">
            <div className="pp-cal-hdr">Dates</div>
            <div className="pp-cal-hdr">Event</div>
            <div className="pp-cal-hdr">Priority</div>
          </div>
          <div className="pp-cal-row">
            <div className="pp-cal-date">1–7 June 2026</div>
            <div className="pp-cal-event">Italy Major — Rome, Foro Italico <span>Global broadcast · Red Bull TV + Canal+ · Prestige Major</span></div>
            <div className="pp-cal-tag now">Now</div>
          </div>
          <div className="pp-cal-row">
            <div className="pp-cal-date">8–14 June 2026</div>
            <div className="pp-cal-event">Valencia P1 — Spain <span>Spanish heartland · Tapia &amp; Coello headlining</span></div>
            <div className="pp-cal-tag now">Now</div>
          </div>
          <div className="pp-cal-row">
            <div className="pp-cal-date">13–19 July 2026</div>
            <div className="pp-cal-event">Málaga P1 — Spain <span>15,000+ attendance historically</span></div>
            <div className="pp-cal-tag major">Major</div>
          </div>
          <div className="pp-cal-row">
            <div className="pp-cal-date">3–9 Aug 2026</div>
            <div className="pp-cal-event">London P1 — Olympia <span>First elite padel event ever held in the UK</span></div>
            <div className="pp-cal-tag major">Major</div>
          </div>
          <div className="pp-cal-row">
            <div className="pp-cal-date">31 Aug–6 Sep 2026</div>
            <div className="pp-cal-event">Madrid P1 — WiZink Center <span>35,000 tickets · Biggest single event of the season</span></div>
            <div className="pp-cal-tag major">Major</div>
          </div>
          <div className="pp-cal-row">
            <div className="pp-cal-date">7–13 Sep 2026</div>
            <div className="pp-cal-event">Paris Major — Roland-Garros <span>Prestige Major · Canal+ global broadcast</span></div>
            <div className="pp-cal-tag major">Major</div>
          </div>
          <div className="pp-cal-row">
            <div className="pp-cal-date">7–13 Dec 2026</div>
            <div className="pp-cal-event">Barcelona Finals <span>Season climax · Top 16 only · Global broadcast</span></div>
            <div className="pp-cal-tag major">Season climax</div>
          </div>
        </div>
      </section>

      <hr className="pp-divider" />

      {/* UNIFYING MESSAGE */}
      <section className="pp-section">
        <div className="pp-section-label">What Sport X is</div>
        <div className="pp-message-block">
          <blockquote>&ldquo;Sport X is built for communities who love sport the way it&apos;s supposed to be — safe, social, and cause-led. No gambling. No advertising. No data exploitation. The gambling operators have their products. This is ours.&rdquo;</blockquote>
        </div>
        <p className="pp-mission-note">Sport X is a platform from Jana — a sport-for-good company. Every platform we build is a deliberate rejection of the gambling, advertising, and data-exploitation models that dominate sport commercially. Our name means &ldquo;people&rdquo; in Sanskrit. The cause layer isn&apos;t a feature. It&apos;s what we are.</p>
      </section>

      {/* CTA */}
      <section className="pp-cta-section">
        <div className="pp-cta-title">See it in <em>action.</em></div>
        <p className="pp-cta-sub">Try Sport X as a manager. Five minutes from sign-up to your first auction.</p>
        <div className="pp-cta-buttons">
          <Link to="/" className="pp-btn-primary" data-testid="pp-cta-see-product">Try the app →</Link>
          <a href="mailto:hello@jana.sport" className="pp-btn-secondary" data-testid="pp-cta-get-in-touch">Get in touch</a>
        </div>
        <div className="pp-cta-contact">Partnership enquiries: <a href="mailto:hello@jana.sport">hello@jana.sport</a></div>
      </section>

      {/* FOOTER */}
      <footer className="pp-footer">
        <div className="pp-footer-logo">SPORT <span>X</span> · Jana</div>
        <div className="pp-footer-note">Not a gambling product. No real money involved. © 2026 Jana</div>
      </footer>

    </div>
  );
}
